import React, { useEffect, useMemo, useState } from "react";
import { HelpCircle, Search as MagIcon, ThumbsUp, ThumbsDown, Loader2, Plus, X, Send, ChevronDown, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CAT_LABEL = {
  general_knowledge: "General Knowledge",
  health:            "Health",
  safety:            "Safety",
  tech:              "Tech · Internet",
  finance:           "Finance",
  education:         "Education · Study",
  travel:            "Travel",
  environment:       "Environment",
  sports:            "Sports · Fitness",
  life_skills:       "Life Skills",
};

export default function Faqs() {
  const { user } = useAuth();
  const [meta, setMeta] = useState({ categories: [] });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [showAsk, setShowAsk] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [speakingId, setSpeakingId] = useState(null);

  // Text-to-speech using the browser's built-in speechSynthesis. Cancels any
  // ongoing utterance when the user taps a different FAQ. Falls back silently
  // if the browser doesn't expose speechSynthesis (e.g. some in-app WebViews).
  const speak = (id, text) => {
    if (typeof window === "undefined" || !window.speechSynthesis) {
      toast.error("Your browser doesn't support text-to-speech");
      return;
    }
    if (speakingId === id) {
      window.speechSynthesis.cancel();
      setSpeakingId(null);
      return;
    }
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.98; u.pitch = 1.0; u.volume = 1.0;
    // Prefer an English voice if available for consistent pronunciation.
    const voices = window.speechSynthesis.getVoices();
    u.voice = voices.find(v => /en[-_]IN/i.test(v.lang))
           || voices.find(v => /en[-_]/i.test(v.lang))
           || voices[0];
    u.onend = () => setSpeakingId(null);
    u.onerror = () => setSpeakingId(null);
    setSpeakingId(id);
    window.speechSynthesis.speak(u);
  };
  useEffect(() => () => { window.speechSynthesis?.cancel(); }, []);

  useSeo({
    title: "Worldwide FAQs · Educational · find2near",
    description: "Curated worldwide FAQs — health, safety, tech, finance, travel, environment, sports and life skills. Ask your own questions, get community answers.",
    canonical: "/faqs",
  });

  useEffect(() => { api.get("/faqs/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  const load = () => {
    setLoading(true);
    const params = {};
    if (cat) params.category = cat;
    if (q)   params.q = q;
    api.get("/faqs", { params })
       .then((r) => setItems(r.data))
       .catch(() => setItems([]))
       .finally(() => setLoading(false));
  };
  useEffect(load, [cat]); // eslint-disable-line

  const grouped = useMemo(() => {
    const g = {};
    for (const f of items) (g[f.category] = g[f.category] || []).push(f);
    return g;
  }, [items]);

  const react = async (id, kind) => {
    if (!user) { toast.error("Sign in to react"); return; }
    try { await api.post(`/faqs/${id}/react`, { kind }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6" data-testid="faqs-page">
      <AskForm open={showAsk} onClose={() => setShowAsk(false)} onSaved={() => { setShowAsk(false); load(); }} />

      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2"><HelpCircle size={12} className="text-sky-600" /> Educational · Worldwide FAQs</div>
          <h1 className="heading text-3xl md:text-4xl font-black text-stone-900">Ask anything. <span className="text-sky-600">Learn everything.</span></h1>
          <p className="text-stone-500 text-sm mt-1">Curated answers across health, safety, tech, finance, travel, environment, sports and life skills.</p>
        </div>
        {user && (
          <Button onClick={() => setShowAsk(true)} className="rounded-full bg-sky-600 hover:bg-sky-700 font-black" data-testid="faq-ask-btn">
            <Plus size={14} className="mr-1.5" /> Ask a question
          </Button>
        )}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="bg-white border border-stone-200 rounded-3xl p-3 flex flex-wrap gap-2 items-center mb-4">
        <div className="flex items-center gap-2 flex-1 min-w-[220px] bg-stone-50 rounded-2xl px-3 py-1.5">
          <MagIcon size={14} className="text-stone-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search FAQs, e.g. 'CPR' or 'compound interest'…" className="bg-transparent w-full outline-none text-sm" data-testid="faq-q" />
        </div>
        <Select value={cat || "__all__"} onValueChange={(v) => setCat(v === "__all__" ? "" : v)}>
          <SelectTrigger className="rounded-full w-56 h-9 text-xs" data-testid="faq-cat"><SelectValue placeholder="All categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All categories</SelectItem>
            {(meta.categories || []).map((c) => <SelectItem key={c.key} value={c.key}>{CAT_LABEL[c.key] || c.key} · {c.count}</SelectItem>)}
          </SelectContent>
        </Select>
      </form>

      {loading ? (
        <div className="text-center py-16 text-stone-400"><Loader2 className="mx-auto animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-stone-400 border border-dashed border-stone-200 rounded-3xl">No FAQs match this filter.</div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([category, list]) => (
            <section key={category}>
              <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mb-2">{CAT_LABEL[category] || category} · {list.length}</div>
              <div className="grid gap-2">
                {list.map((f) => {
                  const open = openId === f.faq_id;
                  return (
                    <article key={f.faq_id} className="rounded-3xl bg-white border border-stone-200" data-testid={`faq-${f.faq_id}`}>
                      <button onClick={() => setOpenId(open ? null : f.faq_id)} className="w-full text-left p-4 flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="font-black text-stone-900">{f.q}</div>
                          {!open && f.a && <p className="text-xs text-stone-500 mt-1 line-clamp-2">{f.a}</p>}
                          {f.is_user && <span className="text-[10px] font-black uppercase tracking-widest text-amber-800 bg-amber-100 border border-amber-200 rounded-full px-1.5 py-0.5 mt-1 inline-block">community</span>}
                        </div>
                        <ChevronDown size={16} className={`text-stone-400 transition-transform ${open ? "rotate-180" : ""}`} />
                      </button>
                      {open && (
                        <div className="px-4 pb-4 -mt-1 border-t border-stone-100 pt-3">
                          {f.a
                            ? <p className="text-sm text-stone-700 whitespace-pre-line leading-relaxed">{f.a}</p>
                            : <p className="text-sm text-stone-400 italic">No curated answer yet — community answers below.</p>}

                          {(f.answers || []).length > 0 && (
                            <div className="mt-3 space-y-2">
                              <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Community answers</div>
                              {f.answers.map((ans, i) => (
                                <div key={i} className="rounded-xl bg-stone-50 border border-stone-200 p-3 text-sm text-stone-700">
                                  <div className="text-[11px] text-stone-500 mb-1">by <b>{ans.name}</b></div>
                                  {ans.a}
                                </div>
                              ))}
                            </div>
                          )}

                          <div className="mt-3 flex items-center gap-2 text-xs">
                            <button onClick={() => react(f.faq_id, "up")}   className="rounded-full px-2 py-1 hover:bg-emerald-50 text-emerald-700 font-bold inline-flex items-center gap-1" data-testid={`faq-up-${f.faq_id}`}><ThumbsUp size={12}/> {f.reactions?.up || 0}</button>
                            <button onClick={() => react(f.faq_id, "down")} className="rounded-full px-2 py-1 hover:bg-stone-50 text-stone-600 font-bold inline-flex items-center gap-1"><ThumbsDown size={12}/> {f.reactions?.down || 0}</button>
                            {f.a && (
                              <button onClick={() => speak(f.faq_id, `${f.q}. ${f.a}`)}
                                      className={`rounded-full px-2 py-1 hover:bg-sky-50 font-bold inline-flex items-center gap-1 ${speakingId === f.faq_id ? "text-sky-700 bg-sky-50" : "text-sky-600"}`}
                                      data-testid={`faq-speak-${f.faq_id}`}>
                                {speakingId === f.faq_id ? <VolumeX size={12}/> : <Volume2 size={12}/>}
                                {speakingId === f.faq_id ? "Stop" : "Listen"}
                              </button>
                            )}
                            {(f.tags || []).map((t) => <span key={t} className="text-[10px] font-bold text-stone-500">#{t}</span>)}
                          </div>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function AskForm({ open, onClose, onSaved }) {
  const [q, setQ] = useState("");
  const [a, setA] = useState("");
  const [cat, setCat] = useState("general_knowledge");
  const [tags, setTags] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;
  const submit = async () => {
    if (q.trim().length < 8) { toast.error("Question is too short"); return; }
    setBusy(true);
    try {
      await api.post("/faqs", { q, a, category: cat, tags: tags.split(/[,\s]+/).filter(Boolean) });
      toast.success("Question posted");
      setQ(""); setA(""); setTags("");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200"><b>Ask a question</b><button onClick={onClose}><X size={18} className="text-stone-500" /></button></div>
        <div className="p-5 space-y-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Your question…" className="rounded-2xl h-10" data-testid="ask-q" />
          <textarea value={a} onChange={(e) => setA(e.target.value)} rows={5} placeholder="Optional — your own answer or context (if you have one)" className="w-full px-4 py-3 rounded-2xl border border-stone-200 outline-none text-sm" data-testid="ask-a" />
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger className="rounded-2xl h-10"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(CAT_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
          <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="Hashtags (comma or space separated)" className="rounded-2xl h-10 font-mono text-xs" />
          <Button onClick={submit} disabled={busy} className="w-full rounded-full bg-sky-600 hover:bg-sky-700 font-black"><Send size={14} className="mr-2" /> Post</Button>
        </div>
      </div>
    </div>
  );
}
